import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import {
  fetchAllGroupsBalanceData,
  calculateUserBalanceForGroups,
  calculateGroupStats,
} from '@/api/balances';
import type { GroupWithMembers } from '@/api/groups';
import type { UserBalance } from '@/hooks/useBalances';
import type { GroupStats } from '@/api/balances';

interface DashboardBalanceData {
  balances: Map<string, UserBalance>;
  stats: Map<string, GroupStats>;
}

/**
 * Batch-fetch and calculate the current user's balance + group stats for all dashboard groups.
 * Single fetch, two derivations.
 */
export function useDashboardBalances(groups: GroupWithMembers[] | undefined) {
  const { user } = useAuth();

  return useQuery<DashboardBalanceData>({
    queryKey: ['dashboard-balances', user?.id],
    queryFn: async () => {
      const groupIds = groups!.map(g => g.id);
      const data = await fetchAllGroupsBalanceData(groupIds);
      return {
        balances: calculateUserBalanceForGroups(user!.id, groups!, data),
        stats: calculateGroupStats(groups!, data),
      };
    },
    enabled: !!user && !!groups && groups.length > 0,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    refetchOnWindowFocus: true,
  });
}
